import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../reducers';
import { FileType } from '../../../types';
import { screenAppAPIClient } from '../../../lib/baseAxios';

// Types for the backend response
interface AssistantSearchResponse {
  searchResults: FileType[];
  aiAnswer: string;
  featuredThumbnail?: string;
  thumbnails?: Record<string, string>;
  processingTime: number;
  queryOptimization?: {
    originalQuery: string;
    optimizedQuery: string;
  };
}

interface AssistantSearchRequest {
  query: string;
  folderId?: string;
  includeThumbnails?: boolean;
  limit?: number;
  context?: {
    currentFileId?: string;
    userSettings?: {
      name?: string;
      role?: string;
      teamName?: string;
      customVocabulary?: string;
      defaultLanguage?: string;
    };
  };
}

interface UseBackendAssistantSearchReturn {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  results: AssistantSearchResponse | null;
  error: string | null;
  isGeneratingAnswer: boolean;
  performSearch: (
    query: string,
    context?: AssistantSearchRequest['context']
  ) => Promise<void>;
  reset: () => void;
}

const useBackendAssistantSearch = (): UseBackendAssistantSearchReturn => {
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'succeeded' | 'failed'
  >('idle');
  const [results, setResults] = useState<AssistantSearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGeneratingAnswer, setIsGeneratingAnswer] = useState(false);

  // Get user and team info from Redux
  const userSettings = useSelector((state: RootState) => state.settings.user);
  const teamState = useSelector((state: RootState) => state.team.selectedTeam);
  const recorderSettings = useSelector(
    (state: RootState) => state.settings.recorder
  );
  const selectedVideo = useSelector(
    (state: RootState) => state.selectedVideo?.file
  );

  // Get URL parameters
  const { teamId, folderId } = useParams<{
    teamId?: string;
    folderId?: string;
  }>();

  const performSearch = useCallback(
    async (query: string, context?: AssistantSearchRequest['context']) => {
      if (!query.trim() || !teamId) {
        setError('Query and team ID are required');
        return;
      }

      setStatus('loading');
      setError(null);
      setIsGeneratingAnswer(false);

      const requestBody: AssistantSearchRequest = {
        query: query.trim(),
        folderId,
        includeThumbnails: true,
        limit: 15, // limit is the number of files to return
        context: {
          currentFileId: selectedVideo?._id,
          userSettings: {
            name: userSettings?.name,
            role: userSettings?.role,
            teamName: teamState?.name,
            customVocabulary: teamState?.fileOptions?.customVocabulary,
            defaultLanguage:
              recorderSettings?.otherSettings?.defaultTranscriptionLanguage,
          },
          ...context,
        },
      };

      try {
        // Use the screenAppAPIClient's internal API instance
        const response = await (screenAppAPIClient as any).api.post(
          `/v2/files/assistant-search/${teamId}`,
          requestBody
        );

        if (response.data.success) {
          setResults(response.data.data);
          setStatus('succeeded');
          // If we have search results but no AI answer, set generating state
          if (
            response.data.data.searchResults &&
            response.data.data.searchResults.length > 0 &&
            !response.data.data.aiAnswer
          ) {
            setIsGeneratingAnswer(true);
          } else {
            // Clear generating state if we have an AI answer
            setIsGeneratingAnswer(false);
          }
        } else {
          setError(response.data.message || 'Search failed');
          setStatus('failed');
        }
      } catch (err: any) {
        console.error('Assistant search error:', {
          error: err,
          message: err.message,
          response: err.response,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          requestBody,
          teamId,
          url: `/v2/files/assistant-search/${teamId}`,
          baseURL: (screenAppAPIClient as any).api.defaults.baseURL,
        });

        // Provide more detailed error messages
        let errorMessage = 'Search failed';

        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        }

        setError(errorMessage);
        setStatus('failed');
      }
    },
    [teamId, folderId, selectedVideo, userSettings, teamState, recorderSettings]
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setResults(null);
    setError(null);
    setIsGeneratingAnswer(false);
  }, []);

  // Clear generating state when we receive an AI answer
  useEffect(() => {
    if (results?.aiAnswer && isGeneratingAnswer) {
      setIsGeneratingAnswer(false);
    }
  }, [results?.aiAnswer, isGeneratingAnswer]);

  return {
    status,
    results,
    error,
    isGeneratingAnswer,
    performSearch,
    reset,
  };
};

export default useBackendAssistantSearch;
